import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Target, Calendar, FileText, CheckCircle } from "lucide-react";
import { API_BASE_URL } from "../../lib/config";

const API_URL = API_BASE_URL;

// Test categories for assignment
const TEST_CATEGORIES = [
  { id: "staar_3", name: "STAAR Grade 3", topics: ["Reading Comprehension", "Main Idea", "Math Facts", "Addition", "Subtraction", "Word Problems"] },
  { id: "staar_4", name: "STAAR Grade 4", topics: ["Reading Comprehension", "Inference", "Math Operations", "Fractions", "Writing Conventions", "Grammar"] },
  { id: "staar_5", name: "STAAR Grade 5", topics: ["Reading Analysis", "Vocabulary", "Math Problem Solving", "Decimals", "Science Concepts", "Life Science"] },
  { id: "staar_6", name: "STAAR Grade 6", topics: ["Literary Analysis", "Informational Text", "Ratios", "Proportions", "Algebraic Expressions", "Geometry Basics"] },
  { id: "staar_7", name: "STAAR Grade 7", topics: ["Reading Synthesis", "Author's Purpose", "Equations", "Probability", "Writing Structure", "Grammar Rules"] },
  { id: "staar_8", name: "STAAR Grade 8", topics: ["Complex Text Analysis", "Algebra Foundations", "Scientific Method", "Earth Science", "Texas History", "Civics Basics"] },
  { id: "staar_eoc", name: "STAAR EOC", topics: ["English I Literature", "English II Analysis", "Algebra I", "Biology", "U.S. History", "Government"] },
  { id: "sat", name: "SAT", topics: ["Algebra", "Geometry", "Reading", "Vocabulary", "Grammar", "Data Analysis"] },
  { id: "act", name: "ACT", topics: ["Trigonometry", "Algebra", "English", "Reading", "Science", "Statistics"] },
  { id: "asvab", name: "ASVAB", topics: ["Arithmetic Reasoning", "Mathematics Knowledge", "Word Knowledge", "Paragraph Comprehension"] },
];

const CreateAssignment = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    class_id: "",
    title: "",
    category: "",
    topics: [],
    question_count: 10,
    due_date: "",
    instructions: ""
  });

  const getTeacherToken = () => localStorage.getItem("teacherToken");

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    const token = getTeacherToken();
    if (!token) {
      navigate("/teacher/login");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/teacher/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setClasses(await res.json());
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.class_id || !formData.title || !formData.category) {
      alert("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      const token = getTeacherToken();
      const res = await fetch(`${API_URL}/teacher/assignments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSuccess(true);
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to create assignment");
      }
    } catch (error) {
      console.error("Error creating assignment:", error);
    } finally {
      setCreating(false);
    }
  };

  const toggleTopic = (topic) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.includes(topic)
        ? prev.topics.filter(t => t !== topic)
        : [...prev.topics, topic]
    }));
  };

  const selectedCategory = TEST_CATEGORIES.find(c => c.id === formData.category);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-slate-800 mb-2">Assignment Created!</h1>
          <p className="text-slate-600 mb-6">
            Your students can now see and complete this assignment.
          </p>
          <div className="flex gap-3">
            <Link
              to="/teacher/dashboard"
              className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200"
            >
              Dashboard
            </Link>
            <button
              onClick={() => {
                setSuccess(false);
                setFormData({
                  class_id: "",
                  title: "",
                  category: "",
                  topics: [],
                  question_count: 10,
                  due_date: "",
                  instructions: ""
                });
              }}
              className="flex-1 px-4 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700"
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-6">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/teacher/dashboard" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl">Create Assignment</h1>
              <p className="text-indigo-200">Assign practice to your students</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border-2 border-slate-200 p-6 space-y-6">
          {/* Class Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Class *</label>
            <select
              value={formData.class_id}
              onChange={(e) => setFormData({...formData, class_id: e.target.value})}
              className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0"
              required
            >
              <option value="">Choose a class...</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name} ({cls.student_count} students)
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Assignment Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full p-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0"
              placeholder="e.g., Fractions Practice Week 3"
              required
            />
          </div>

          {/* Test Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Test Category *</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TEST_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setFormData({...formData, category: cat.id, topics: []})}
                  className={`p-3 rounded-xl text-left transition-colors ${
                    formData.category === cat.id
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  <p className="font-medium text-sm">{cat.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Topic Selection (optional) */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Focus on Specific Topics (optional)
              </label>
              <p className="text-xs text-slate-500 mb-3">Leave empty to include all topics</p>
              <div className="flex flex-wrap gap-2">
                {selectedCategory.topics.map(topic => (
                  <button
                    key={topic}
                    type="button"
                    onClick={() => toggleTopic(topic)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      formData.topics.includes(topic)
                        ? "bg-lime-500 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Question Count */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Number of Questions</label>
            <div className="flex gap-2">
              {[5, 10, 15, 20, 25].map(num => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setFormData({...formData, question_count: num})}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    formData.question_count === num
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Due Date (optional)</label>
            <div className="relative">
              <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0"
              />
            </div>
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Instructions (optional)</label>
            <div className="relative">
              <FileText className="w-5 h-5 text-slate-400 absolute left-3 top-3" />
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0 resize-none h-24"
                placeholder="Any special instructions for students..."
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={creating}
              className="w-full py-4 bg-lime-500 text-white font-bold rounded-xl hover:bg-lime-600 transition-colors disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Assignment"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateAssignment;
